import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Identifier, useGetOne, useNotify } from 'react-admin';
import {
  AtSign,
  ChevronRight,
  ExternalLink,
  Forward,
  Loader2,
  Mail,
  Paperclip,
  Reply,
  ReplyAll,
  Wand2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Client } from '../../types';
import { EmailDeliveryStatusBadge } from '../../components/EmailDeliveryStatusBadge';
import { EmailComposeDialog, CaseEmailRecipient } from '../../components/EmailComposeDialog';
import { translateText } from './translateApi';
import { CasePartnerChip } from './PartnerRecipientChip';
import { EmailAttachmentList } from './EmailAttachmentList';
import { EmailHtmlBody } from './EmailHtmlBody';
import {
  EmailThread,
  EmailThreadMessage,
  attachmentCountOf,
  cleanBody,
  communicationEmailIds,
  emailPlainText,
  gmailThreadUrl,
  htmlOnlyBody,
  snippetOf,
  splitQuotedText,
} from '../emailThreads';

// ─── Address book ────────────────────────────────────────────────────────────

export interface ThreadClient {
  id: number;
  name: string;
  email?: string | null;
}

const norm = (email: string | null | undefined) => (email || '').trim().toLowerCase();
/** partners.email may hold a comma-separated list. */
const partnerAddresses = (p: CasePartnerChip) =>
  (p.email || '').split(/[,;]/).map(norm).filter(Boolean);

function useAddressBook(clients: ThreadClient[], partners: CasePartnerChip[]) {
  return useMemo(() => {
    const clientByEmail = new Map<string, ThreadClient>();
    for (const c of clients) if (c.email) clientByEmail.set(norm(c.email), c);
    const partnerByEmail = new Map<string, CasePartnerChip>();
    for (const p of partners) for (const a of partnerAddresses(p)) partnerByEmail.set(a, p);
    // Partner staff often write from their own address on the partner's domain.
    const partnerByDomain = new Map<string, CasePartnerChip>();
    for (const p of partners) {
      for (const a of partnerAddresses(p)) {
        const domain = a.split('@')[1];
        if (domain && !FREE_MAIL.has(domain)) partnerByDomain.set(domain, p);
      }
    }
    /** The case partner an address belongs to: exact address, the role the feed filed the row under, then domain. */
    const partnerFor = (email: string | null | undefined, role?: string | null) =>
      partnerByEmail.get(norm(email)) ||
      partners.find((p) => role && p.role === role) ||
      partnerByDomain.get(norm(email).split('@')[1] || '');
    const nameFor = (email: string | null | undefined, fallback?: string | null) => {
      const e = norm(email);
      if (isCaseAddress(e)) return 'Cleo';
      return clientByEmail.get(e)?.name || fallback || partnerByEmail.get(e)?.name || email || 'Unknown';
    };
    return { clientByEmail, partnerFor, nameFor };
  }, [clients, partners]);
}

/** A per-case reply address (c-xxxx@cases.cleocremation.com): mail to it is mail to us. */
const isCaseAddress = (email: string | null | undefined) => /@cases\./i.test(email || '');

const FREE_MAIL = new Set(['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'yahoo.ca', 'live.com', 'icloud.com', 'videotron.ca', 'sympatico.ca', 'bell.net']);

const firstName = (name: string) => (name.includes('@') ? name.split('@')[0] : name.split(/\s+/)[0]);

const shortTime = (ts: string) =>
  new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const shortDate = (ts: string) => {
  const d = new Date(ts);
  return d.toDateString() === new Date().toDateString()
    ? shortTime(ts)
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const longDate = (ts: string) =>
  new Date(ts).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

// ─── Feed card ───────────────────────────────────────────────────────────────

interface EmailThreadCardProps {
  thread: EmailThread;
  clients: ThreadClient[];
  partners: CasePartnerChip[];
  clientColor?: string;
  clientName?: string;
  onOpen: (thread: EmailThread) => void;
}

/** One card per email thread in the case feed; opens EmailThreadModal. */
export function EmailThreadCard({
  thread,
  clients,
  partners,
  clientColor,
  clientName,
  onOpen,
}: EmailThreadCardProps) {
  const { nameFor } = useAddressBook(clients, partners);
  const latest = thread.latest;
  const isOutbound = !latest.inbound;
  const count = thread.messages.length;
  const fromLabel = nameFor(latest.from, latest.fromName);
  const toNames = latest.to.map((a) => firstName(nameFor(a)));
  const toLabel = toNames.join(', ') + (latest.cc.length ? ` +${latest.cc.length}` : '');
  const snippet = snippetOf(emailPlainText(latest.event));

  return (
    <div
      className={cn('flex flex-col gap-1', isOutbound ? 'ml-auto items-end' : 'mr-auto items-start')}
      style={{ maxWidth: 'min(90%, 26rem)' }}
    >
      <button
        type="button"
        onClick={() => onOpen(thread)}
        aria-haspopup="dialog"
        data-email-thread={thread.id}
        className={cn(
          'w-full text-left rounded-2xl border px-3 sm:px-4 py-2.5 text-sm shadow-sm transition-colors',
          'hover:border-foreground/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isOutbound
            ? 'bg-primary/5 border-primary/20 rounded-br-md'
            : 'bg-muted border-border rounded-bl-md',
        )}
        style={clientColor ? { borderLeft: `3px solid ${clientColor}` } : undefined}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <Mail className="h-3.5 w-3.5 text-purple-600 shrink-0" />
          <span className="text-xs font-semibold flex-1 min-w-0 truncate">{thread.subject}</span>
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
          <span className="font-medium text-foreground/80">{fromLabel}</span>
          {toLabel && <> → {toLabel}</>}
        </div>
        {snippet && (
          <div className="mt-1 text-foreground/90 leading-snug line-clamp-2 [overflow-wrap:anywhere]">
            {snippet}
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {count > 1 && (
            <span className="inline-flex items-center gap-1 rounded-full border bg-background px-1.5 py-0.5 text-[11px] font-medium tabular-nums">
              <Mail className="h-3 w-3" />
              Thread · {count}
            </span>
          )}
          {thread.attachmentCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground tabular-nums">
              <Paperclip className="h-3 w-3" />
              {thread.attachmentCount}
            </span>
          )}
          {thread.needsReply && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
              Needs reply
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
            Open <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      </button>

      <div className="flex items-center gap-1.5 px-1">
        <span className="text-[11px] text-muted-foreground">{shortTime(latest.event.timestamp)}</span>
        {clientName && <span className="text-[11px] text-muted-foreground">&middot; {clientName}</span>}
        {isOutbound && latest.event.user_name && (
          <span className="text-[11px] text-muted-foreground">&middot; {latest.event.user_name}</span>
        )}
        {isOutbound && <EmailDeliveryStatusBadge metadata={latest.event.metadata} />}
      </div>
    </div>
  );
}

// ─── Thread modal ────────────────────────────────────────────────────────────

type ComposeKind = 'reply' | 'replyAll' | 'forward';

interface ComposeState {
  kind: ComposeKind;
  recipient: { name?: string; email?: string | null };
  context: { funeralId?: number; leadId?: string; clientId?: number };
  caseRecipient?: CaseEmailRecipient;
  alsoTo?: number[];
  ccDefault?: string;
  inReplyToCommunicationId?: string;
  subject: string;
  text?: string;
}

interface EmailThreadModalProps {
  thread: EmailThread | null;
  onClose: () => void;
  /** The case this thread is on. Absent in the lead view and inbox (see leadId). */
  funeralId?: number;
  /**
   * No funeralId: a reply is an ordinary send from support@, filed on this lead
   * and person, written to the sender whether or not they're a known contact.
   */
  leadId?: string;
  clientId?: number;
  deceasedClientId?: Identifier | null;
  clients: ThreadClient[];
  partners: CasePartnerChip[];
  /** Null while case_email_reply_to_enabled is off: no Copy case email. */
  caseAddress: string | null;
  /** A reply went out, so the feed underneath should reload. */
  onSent?: () => void;
}

const isOurAddress = (email: string) => /@(cases\.)?cleocremation\.com$/i.test(email);

const withPrefix = (prefix: 'Re' | 'Fwd', subject: string) =>
  new RegExp(`^${prefix}:`, 'i').test(subject.trim()) ? subject.trim() : `${prefix}: ${subject.trim()}`;

export function EmailThreadModal({
  thread,
  onClose,
  funeralId,
  leadId,
  clientId,
  deceasedClientId,
  clients,
  partners,
  caseAddress,
  onSent,
}: EmailThreadModalProps) {
  const onCase = funeralId != null;
  const notify = useNotify();
  const book = useAddressBook(clients, partners);
  const [compose, setCompose] = useState<ComposeState | null>(null);
  const [openIdx, setOpenIdx] = useState<Set<number>>(new Set());
  const bodyRef = useRef<HTMLDivElement>(null);

  // Newest message open, the rest collapsed, every time a thread opens.
  useEffect(() => {
    if (thread) setOpenIdx(new Set([thread.messages.length - 1]));
  }, [thread]);

  const { data: deceased } = useGetOne<Client>(
    'clients',
    { id: deceasedClientId ?? 0 },
    { enabled: !!thread && !!deceasedClientId },
  );

  // Who a reply goes to: the last person who wrote in, else whoever we last wrote to.
  const replyTarget = useMemo(() => {
    if (!thread) return null;
    const lastIn = [...thread.messages].reverse().find((m) => m.inbound);
    const address = lastIn ? lastIn.from : thread.latest.to[0];
    const client = book.clientByEmail.get(norm(address));
    if (client) return { kind: 'client' as const, client, address };
    const partner = book.partnerFor(address, (lastIn ?? thread.latest).event.recipient_type);
    if (partner) return { kind: 'partner' as const, partner, address };
    return { kind: 'none' as const, address };
  }, [thread, book]);

  // Reply all: the other case clients on the thread. A partner email goes to
  // the partner and nobody else (EmailComposeDialog's rule), so none for them.
  const replyAllOthers = useMemo(() => {
    if (!thread || replyTarget?.kind !== 'client') return [];
    const ids = new Set<number>();
    for (const m of thread.messages) {
      for (const a of [m.from, ...m.to, ...m.cc]) {
        const c = book.clientByEmail.get(norm(a));
        if (c && c.id !== replyTarget.client.id) ids.add(c.id);
      }
    }
    return [...ids];
  }, [thread, replyTarget, book]);

  // Off a case: everyone else on the thread, as a CC the sender can still edit.
  const leadReplyAllCc = useMemo(() => {
    if (!thread || onCase) return [];
    const skip = new Set([norm(replyTarget?.address)]);
    const cc = new Set<string>();
    for (const m of thread.messages) {
      for (const a of [m.from, ...m.to, ...m.cc]) {
        const e = norm(a);
        if (e.includes('@') && !skip.has(e) && !isOurAddress(e)) cc.add(e);
      }
    }
    return [...cc];
  }, [thread, onCase, replyTarget]);

  const participants = useMemo(() => {
    if (!thread) return [];
    const seen = new Map<string, string>();
    for (const m of thread.messages) {
      if (m.from && !seen.has(norm(m.from))) seen.set(norm(m.from), book.nameFor(m.from, m.fromName));
      for (const a of [...m.to, ...m.cc]) {
        if (a && !isCaseAddress(a) && !seen.has(norm(a))) seen.set(norm(a), book.nameFor(a));
      }
    }
    return [...seen.entries()].map(([email, name]) => ({ email, name }));
  }, [thread, book]);

  const startCompose = useCallback(
    (kind: ComposeKind) => {
      if (!thread) return;
      if (kind === 'forward') {
        const m = thread.latest;
        const header = [
          '---------- Forwarded message ----------',
          `From: ${book.nameFor(m.from, m.fromName)}${m.from ? ` <${m.from}>` : ''}`,
          `Date: ${longDate(m.event.timestamp)}`,
          `Subject: ${m.event.subject || thread.subject}`,
          m.to.length ? `To: ${m.to.join(', ')}` : '',
        ].filter(Boolean);
        setCompose({
          kind,
          recipient: {},
          // A forward goes to a free-text address, so it's not a case send.
          context: {},
          subject: withPrefix('Fwd', thread.subject),
          text: `\n\n${header.join('\n')}\n\n${cleanBody(emailPlainText(m.event))}`,
        });
      } else {
        const subject = withPrefix('Re', thread.latest.event.subject || thread.subject);
        if (!onCase) {
          if (!replyTarget?.address) return;
          // Answer what they wrote, else the newest message we hold.
          const parent = [...thread.messages].reverse().find((m) => m.inbound) ?? thread.latest;
          const person = replyTarget.kind === 'client' ? replyTarget.client : null;
          setCompose({
            kind,
            recipient: { name: person?.name, email: replyTarget.address },
            context: { leadId, clientId: person?.id ?? clientId },
            ccDefault: kind === 'replyAll' ? leadReplyAllCc.join(', ') : undefined,
            inReplyToCommunicationId: communicationEmailIds([parent.event])[0],
            subject,
          });
          onClose();
          return;
        }
        if (!replyTarget || replyTarget.kind === 'none') return;
        if (replyTarget.kind === 'partner') {
          const p = replyTarget.partner;
          setCompose({
            kind,
            recipient: { name: p.name, email: p.email },
            context: { funeralId },
            caseRecipient: { kind: 'partner', partnerId: p.partnerId, name: p.name, email: p.email || '' },
            subject,
          });
        } else {
          const c = replyTarget.client;
          setCompose({
            kind,
            recipient: { name: c.name, email: c.email },
            context: { funeralId, clientId: c.id },
            caseRecipient: { kind: 'client', clientId: c.id },
            alsoTo: kind === 'replyAll' ? replyAllOthers : [],
            subject,
          });
        }
      }
      // The compose dialog is MUI; the thread modal's focus trap would block it.
      onClose();
    },
    [thread, replyTarget, replyAllOthers, leadReplyAllCc, onCase, funeralId, leadId, clientId, book, onClose],
  );

  const canReply = onCase
    ? !!replyTarget && replyTarget.kind !== 'none'
    : !!replyTarget?.address;
  const canReplyAll = canReply && (onCase ? replyAllOthers.length > 0 : leadReplyAllCc.length > 0);
  const replyBlocked = !canReply
    ? onCase
      ? `${replyTarget?.address || 'The sender'} isn't a client or partner on this case. Reply from Gmail.`
      : 'This email has no address to reply to. Reply from Gmail.'
    : undefined;
  const replyAllBlocked = !canReply
    ? replyBlocked
    : replyTarget?.kind === 'partner'
      ? 'Partner emails go to the partner only.'
      : !canReplyAll
        ? onCase
          ? 'Nobody else from the case is on this thread.'
          : 'Nobody else is on this thread.'
        : undefined;

  const onKeyDown = (e: React.KeyboardEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest('input, textarea, [contenteditable="true"]') || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'r' && canReply) startCompose('reply');
    else if (e.key === 'a' && canReplyAll) startCompose('replyAll');
    else if (e.key === 'f') startCompose('forward');
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const heads = Array.from(bodyRef.current?.querySelectorAll<HTMLButtonElement>('[data-thread-msg]') || []);
      if (!heads.length) return;
      e.preventDefault();
      const i = heads.indexOf(document.activeElement as HTMLButtonElement);
      const next = e.key === 'ArrowDown' ? Math.min(heads.length - 1, i + 1) : Math.max(0, i === -1 ? heads.length - 1 : i - 1);
      heads[next].focus();
    }
  };

  const copyCaseAddress = async () => {
    if (!caseAddress) return;
    try {
      await navigator.clipboard.writeText(caseAddress);
      notify(`Copied ${caseAddress}. BCC it to file your email on this case.`, { type: 'success' });
    } catch {
      notify(caseAddress, { type: 'info' });
    }
  };

  const gmailUrl = thread ? gmailThreadUrl(thread) : null;

  return (
    <>
      <Dialog open={!!thread} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          hideClose
          onKeyDown={onKeyDown}
          className={cn(
            'flex flex-col gap-0 p-0 overflow-hidden w-[calc(100%-2rem)] max-w-3xl max-h-[88vh] sm:rounded-xl',
            // Phone: a full-screen sheet, pinned to the screen even when the
            // page underneath is wider than it.
            'max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-[100dvw] max-sm:max-w-none',
            'max-sm:h-[100dvh] max-sm:max-h-none max-sm:rounded-none max-sm:border-0',
          )}
        >
          {thread && (
            <>
              {/* Header */}
              <div className="border-b px-4 sm:px-5 pt-4 pb-3 space-y-2">
                <div className="flex items-start gap-2">
                  <DialogTitle className="flex-1 min-w-0 text-base sm:text-lg leading-snug [overflow-wrap:anywhere]">
                    {thread.subject}
                  </DialogTitle>
                  {gmailUrl && (
                    <a
                      href={gmailUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Open this thread in Gmail"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      <span className="max-sm:hidden">Open in Gmail</span>
                    </a>
                  )}
                  {caseAddress && (
                    <button
                      type="button"
                      onClick={() => void copyCaseAddress()}
                      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                      title={`${caseAddress}: BCC this from Gmail to file the email on the case`}
                    >
                      <AtSign className="h-3.5 w-3.5" />
                      <span className="max-sm:hidden">Copy case email</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label="Close"
                    title="Close (Esc)"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <DialogDescription className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{onCase ? `Case #${funeralId}` : 'Lead'}</span>
                  {deceased?.name && <span>· {deceased.name}</span>}
                  <span>· {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}</span>
                </DialogDescription>
                <div className="flex flex-wrap gap-1.5">
                  {participants.map((p) => (
                    <span
                      key={p.email}
                      title={p.email}
                      className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-muted px-2 py-0.5 text-xs"
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Messages, oldest first */}
              <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto">
                {thread.messages.map((m, i) => (
                  <ThreadMessage
                    key={String(m.event.id)}
                    message={m}
                    funeralId={funeralId}
                    open={openIdx.has(i)}
                    nameFor={book.nameFor}
                    onToggle={() =>
                      setOpenIdx((prev) => {
                        const next = new Set(prev);
                        if (next.has(i)) next.delete(i);
                        else next.add(i);
                        return next;
                      })
                    }
                  />
                ))}
              </div>

              {/* Actions */}
              <div className="grid grid-cols-3 gap-2 border-t px-3 sm:px-5 py-3 sm:flex sm:items-center pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
                <ActionButton primary icon={Reply} label="Reply" hint="r" disabledReason={replyBlocked} onClick={() => startCompose('reply')} />
                <ActionButton icon={ReplyAll} label="Reply all" hint="a" disabledReason={replyAllBlocked} onClick={() => startCompose('replyAll')} />
                <ActionButton icon={Forward} label="Forward" hint="f" onClick={() => startCompose('forward')} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {compose && (
        <EmailComposeDialog
          open
          onClose={() => setCompose(null)}
          recipient={compose.recipient}
          context={compose.context}
          caseRecipient={compose.caseRecipient}
          ccDefault={compose.ccDefault}
          initialAlsoTo={compose.alsoTo}
          initialSubject={compose.subject}
          initialText={compose.text}
          inReplyToCommunicationId={compose.inReplyToCommunicationId}
          onSent={onSent}
        />
      )}
    </>
  );
}

function ActionButton({
  icon: Icon,
  label,
  hint,
  primary,
  disabledReason,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  hint: string;
  primary?: boolean;
  disabledReason?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!!disabledReason}
      title={disabledReason || `${label} (${hint})`}
      className={cn(
        'inline-flex h-9 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        primary
          ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
          : 'bg-background hover:bg-muted',
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function ThreadMessage({
  message: m,
  funeralId,
  open,
  nameFor,
  onToggle,
}: {
  message: EmailThreadMessage;
  funeralId?: number;
  open: boolean;
  nameFor: (email: string | null | undefined, fallback?: string | null) => string;
  onToggle: () => void;
}) {
  const [showQuoted, setShowQuoted] = useState(false);
  const [translation, setTranslation] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const htmlBody = htmlOnlyBody(m.event);
  const body = cleanBody(emailPlainText(m.event));
  const { main, quoted, hasQuoted } = splitQuotedText(body);
  const sender = nameFor(m.from, m.fromName);
  const attachmentCount = attachmentCountOf(m);

  const handleTranslate = async () => {
    if (translation) {
      setShowTranslation(!showTranslation);
      return;
    }
    if (!main) return;
    setTranslating(true);
    try {
      setTranslation(await translateText(main, 'en', m.event.id));
      setShowTranslation(true);
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-2 px-4 sm:px-5">
        <button
          type="button"
          data-thread-msg
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2.5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          <span
            className={cn(
              'grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold',
              m.inbound ? 'bg-amber-100 text-amber-800' : 'bg-primary/10 text-primary',
            )}
          >
            {sender.slice(0, 1).toUpperCase()}
          </span>
          <span className="flex min-w-0 flex-1 items-baseline gap-2">
            <span className="shrink-0 text-sm font-semibold">{sender}</span>
            {open ? (
              m.from && <span className="truncate text-xs text-muted-foreground max-sm:hidden">&lt;{m.from}&gt;</span>
            ) : (
              <span className="truncate text-xs text-muted-foreground">{snippetOf(emailPlainText(m.event))}</span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground tabular-nums">
            {attachmentCount > 0 && <Paperclip className="h-3 w-3" />}
            {open ? longDate(m.event.timestamp) : shortDate(m.event.timestamp)}
          </span>
        </button>
        {open && main && (
          <button
            type="button"
            onClick={() => void handleTranslate()}
            disabled={translating}
            className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
            title={showTranslation ? 'Hide translation' : 'Translate to English'}
          >
            {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-2.5 px-4 pb-4 sm:pl-[3.75rem] sm:pr-5">
          {(m.to.length > 0 || m.cc.length > 0) && (
            <div className="text-xs text-muted-foreground">
              {m.to.length > 0 && (
                <>to <span className="text-foreground/80">{m.to.map((a) => nameFor(a)).join(', ')}</span></>
              )}
              {m.cc.length > 0 && (
                <> · cc <span className="text-foreground/80">{m.cc.map((a) => nameFor(a)).join(', ')}</span></>
              )}
            </div>
          )}
          {htmlBody ? (
            <EmailHtmlBody html={htmlBody} className="text-sm leading-relaxed" />
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]">
              {main || <span className="italic text-muted-foreground">(no text)</span>}
            </div>
          )}
          {showTranslation && translation && (
            <div className="border-t border-dashed pt-2 text-xs italic whitespace-pre-wrap break-words text-muted-foreground">
              {translation}
            </div>
          )}
          {hasQuoted && !htmlBody &&
            (showQuoted ? (
              <div className="border-l-2 pl-3 text-xs text-muted-foreground whitespace-pre-wrap [overflow-wrap:anywhere]">
                {quoted}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowQuoted(true)}
                className="rounded border bg-muted px-2 text-sm leading-4 tracking-widest text-muted-foreground hover:text-foreground"
                title="Show quoted text"
                aria-label="Show quoted text"
              >
                •••
              </button>
            ))}
          {attachmentCount > 0 && (
            <EmailAttachmentList
              attachments={m.attachments}
              funeralId={funeralId}
              stored={!!m.headers?.attachments_stored}
              unlistedCount={m.attachments.length === 0 ? attachmentCount : 0}
              gmailAvailable={!!m.event.gmail_thread_id}
            />
          )}
        </div>
      )}
    </div>
  );
}
